import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useSearch } from 'src/Utils/search/search.hooks'
import { useSearchContext } from 'src/Utils/search/search.context'
import {
  useQuickSearchQuery,
  useSetQuickSearchQuery,
} from 'src/Utils/search/quickSearch'
import { AdvancedSearchOptions } from 'src/Utils/search/search.types'
import queryString from 'query-string'
import { useLocation } from 'react-router-dom'
import { useNavigateContext } from 'src/components/Reusable/Navigation/NavigateContext'
import {
  CloseIcon,
  SearchBar,
  SearchContainer,
  SearchIcon,
  SearchIconButton,
  SearchIconText,
  SearchInput,
  SearchInputContainer,
  SearchOption,
  SearchOptionsContainer,
  SearchHint,
} from './Search.styles'
import { AdvancedSearch } from './AdvancedSearch/AdvancedSearch'
import { ContentSearch } from './ContentSearch/ContentSearch'

export const Search = ({
  isExpanded,
  setIsExpanded,
  withContentSearch,
  withAdvancedSearch,
  paramPrefix,
  onFocus,
  onBlur,
  onSearch,
  onChange,
  disableEnter,
}: {
  isExpanded: boolean
  setIsExpanded: (isExpanded: boolean) => void
  withContentSearch?: boolean
  withAdvancedSearch?: boolean
  paramPrefix?: string
  onFocus?: () => void
  onBlur?: () => void
  onSearch?: (
    query?: string,
    advancedSearch?: AdvancedSearchOptions,
    isContentSearchActive?: boolean,
  ) => void
  onChange?: (query: string) => void
  disableEnter?: boolean
}): JSX.Element => {
  const searchBarRef = useRef<HTMLDivElement>(null)
  const searchInputRef = useRef<HTMLInputElement>(null)
  const [query, setQuery] = useState('')
  const [isAdvancedSearchOpen, setIsAdvancedSearchOpen] = useState(false)
  const search = useSearch(paramPrefix)
  // Typing filters the list as plain text; Enter runs the full search.
  const quickQuery = useQuickSearchQuery(paramPrefix)
  const setQuickQuery = useSetQuickSearchQuery(paramPrefix)
  // Whether the typed text in the store is this box's, not yet set aside by it.
  const ownsQuickQuery = useRef(false)
  const releaseQuickQuery = useCallback(() => {
    ownsQuickQuery.current = false
    setQuickQuery(null)
  }, [setQuickQuery])
  const { setContentMatchIds } = useSearchContext()
  const location = useLocation()
  const {
    navigateState: { parsedQueryParams },
  } = useNavigateContext()
  const searchParams = useMemo(
    () =>
      parsedQueryParams ||
      queryString.parse(location.search, {
        arrayFormat: 'bracket',
      }),
    [location.search, parsedQueryParams],
  )
  const searchParamValue = useMemo(
    () =>
      searchParams[paramPrefix ? `${paramPrefix}Search` : 'search'] as string,
    [searchParams, paramPrefix],
  )
  const isContentSearchActive = useMemo(() => {
    return (
      searchParams[
        paramPrefix ? `${paramPrefix}ContentSearch` : 'contentSearch'
      ] === 'true'
    )
  }, [searchParams, paramPrefix])

  // Typed text is set aside whenever the search in the URL changes (Enter, clear, a
  // saved filter) and when the box goes away.
  useEffect(() => {
    releaseQuickQuery()
  }, [searchParamValue, releaseQuickQuery])
  useEffect(() => () => releaseQuickQuery(), [releaseQuickQuery])

  // Set aside from outside the box (a page clearing its filters): the box
  // empties too, unless a search in the URL still stands.
  useEffect(() => {
    if (quickQuery !== null || !ownsQuickQuery.current) return
    ownsQuickQuery.current = false
    if (!searchParamValue) {
      setQuery('')
      setIsExpanded(false)
    }
  }, [quickQuery, searchParamValue, setIsExpanded])

  useEffect(() => {
    if (searchParamValue) {
      setQuery(decodeURIComponent(searchParamValue))
      setIsExpanded(true)
    } else if (
      !searchInputRef.current?.contains(document.activeElement) &&
      !isAdvancedSearchOpen
    ) {
      setQuery('')
      setIsExpanded(false)
      setContentMatchIds(new Set())
    }
  }, [
    searchParamValue,
    setIsExpanded,
    setContentMatchIds,
    isAdvancedSearchOpen,
  ])

  // Typing filters as plain text at once; the content search waits for Enter, so
  // say so while there is typed text it has not run on.
  const isContentSearchPending =
    !!withContentSearch &&
    isContentSearchActive &&
    !disableEnter &&
    query.trim() !== '' &&
    query !== decodeURIComponent(searchParamValue ?? '')

  const handleEnter = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && !disableEnter) {
      releaseQuickQuery()
      search(query, undefined, isContentSearchActive)
      onSearch?.(query, undefined, isContentSearchActive)
    }
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setQuery(e.target.value)
    if (!disableEnter) {
      ownsQuickQuery.current = true
      setQuickQuery(e.target.value)
    }
    onChange?.(e.target.value)
  }

  const handleClear = useCallback(() => {
    setQuery('')
    releaseQuickQuery()
    searchParamValue && search('', undefined, isContentSearchActive)
    setContentMatchIds(new Set())
    onChange?.('')
  }, [
    search,
    isContentSearchActive,
    setContentMatchIds,
    onChange,
    searchParamValue,
    releaseQuickQuery,
  ])

  const onClear = useCallback(() => {
    handleClear()
    setIsExpanded(true)
    searchInputRef.current?.focus()
  }, [handleClear, setIsExpanded])

  return (
    <SearchContainer $isExpanded={isExpanded}>
      <SearchBar ref={searchBarRef}>
        <SearchInputContainer>
          <SearchIconButton
            onClick={() => {
              if (isExpanded) {
                // Blur the input first to ensure clean state
                searchInputRef.current?.blur()
                handleClear()
                onBlur?.()
                setIsExpanded(false)
              } else {
                setIsExpanded(true)
                searchInputRef.current?.focus()
              }
            }}
            onMouseDown={(e) => {
              // Prevent default to avoid focus issues
              if (isExpanded) {
                e.preventDefault()
              }
            }}
          >
            <SearchIcon />
            <SearchIconText>Search</SearchIconText>
          </SearchIconButton>
          <SearchInput
            value={query}
            onChange={handleChange}
            onKeyDown={handleEnter}
            ref={searchInputRef}
            onFocus={onFocus}
            onBlur={() => {
              if (!query && !isAdvancedSearchOpen) {
                setIsExpanded(false)
              }
              onBlur?.()
            }}
          />
          {isContentSearchPending && (
            <SearchHint>(enter to search content)</SearchHint>
          )}
          {query.length > 0 && (
            <CloseIcon
              onClick={onClear}
              onMouseDown={(e) => e.preventDefault()}
            />
          )}
        </SearchInputContainer>
        <SearchOptionsContainer>
          {withContentSearch && (
            <SearchOption>
              <ContentSearch query={query} />
            </SearchOption>
          )}
          {withAdvancedSearch && (
            <SearchOption>
              <AdvancedSearch
                anchorRef={searchBarRef}
                query={query}
                onSearch={(query, advancedSearch, isContentSearchActive) => {
                  search(query, advancedSearch, isContentSearchActive)
                  onSearch?.(query, advancedSearch, isContentSearchActive)
                }}
                onMouseDown={(e) => e.preventDefault()}
                onOpenChange={setIsAdvancedSearchOpen}
              />
            </SearchOption>
          )}
        </SearchOptionsContainer>
      </SearchBar>
    </SearchContainer>
  )
}
